import type { GradedAnswer } from "./answer-marks.js";
import { ratings } from "./rating.js";

/**
 * Marking a sentence a reader rewrote, against every rewrite that is right.
 *
 * A grammar exercise's Transform item gives a sentence and asks for it
 * rewritten as the point asks, negated with 没 or turned into a 把 sentence,
 * and a person wrote down every rewrite that would be right. A typed answer
 * that is one of them earns 对. One written in the other writing earns 还行,
 * as the other writing does on a characters card (`./grade-hanzi.ts`).
 * Anything else earns 不对, and the page shows the first rewrite.
 *
 * **Punctuation and spaces are dropped before the comparison.** A reader who
 * leaves off the full stop, or types an ASCII comma where the item has a
 * Chinese one, has written the sentence. The characters themselves have to
 * match, since a different character is a different sentence.
 */

/** Anything a sentence writes that is not one of its characters. */
const unwritten = /[\p{P}\p{Z}\s]/gu;

/** A sentence with its punctuation and spaces taken out, as it is compared. */
export function rewriteKey(sentence: string): string {
  return sentence.normalize("NFC").replaceAll(unwritten, "");
}

/** What a rewrite is weighed against. */
export interface Rewrite {
  readonly typed: string;

  /** Every rewrite that is right, in the article's writing, the one shown first. */
  readonly accepted: readonly string[];

  /** The same rewrites in the other writing. */
  readonly other: readonly string[];
}

/**
 * Mark a rewrite.
 * @param rewrite - What was typed, and what would have been right.
 * @returns The rating, with no marks per character, and the rewrites accepted.
 */
export function gradeRewrite(rewrite: Rewrite): GradedAnswer {
  const typed = rewriteKey(rewrite.typed);
  const keys = (sentences: readonly string[]): ReadonlySet<string> =>
    new Set(sentences.map((sentence) => rewriteKey(sentence)));
  const rating = keys(rewrite.accepted).has(typed)
    ? ratings.good
    : keys(rewrite.other).has(typed) && typed !== ""
      ? ratings.hard
      : ratings.again;

  return { rating, pieces: [], accepted: rewrite.accepted };
}
